import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, X, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';

interface SaStatus {
  active: boolean;
  since:  string | null;
  /** Je superadminem právě tenhle přihlášený token? Rozhoduje server, ne prohlížeč. */
  mine?:  boolean;
}

// Informativní banner nad celým systémem: pokud je připojený správce podpory,
// vidí ho VŠICHNI přihlášení uživatelé klienta. Tlačítko „Ukončit relaci" ale
// dostane jen ten, kdo relaci opravdu drží.
export default function SuperadminBanner() {
  const navigate      = useNavigate();
  const logout        = useAuthStore(s => s.logout);
  const storeFlag     = useAuthStore(s => s.superadmin);
  const setSuperadmin = useAuthStore(s => s.setSuperadmin);
  const [ending, setEnding] = useState(false);

  const { data } = useQuery<SaStatus>({
    queryKey: ['sa-status'],
    queryFn: () => apiClient.get('/auth/superadmin/status').then(r => r.data.data),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Příznak v localStorage je jen pomocný – autoritou je claim v tokenu, který
  // hlásí server. Když se rozejdou (typicky zákazník přihlášený v prohlížeči,
  // kde předtím byla podpora), srovnáme to podle serveru.
  useEffect(() => {
    if (typeof data?.mine === 'boolean' && data.mine !== storeFlag) {
      setSuperadmin(data.mine);
    }
  }, [data?.mine, storeFlag, setSuperadmin]);

  if (!data?.active) return null;

  const isSuperadmin = data.mine === true;

  const endSession = async () => {
    setEnding(true);
    try { await apiClient.post('/auth/superadmin/stop'); } catch { /* i tak se odhlásíme */ }
    logout();
    navigate('/login', { replace: true });
  };

  const sinceTxt = data.since
    ? ` — od ${new Date(data.since).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <div className="w-full bg-amber-500 text-amber-950 px-4 py-2 flex items-center gap-3 text-sm font-medium shadow-sm">
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <span className="flex-1 min-w-0">
        {isSuperadmin ? (
          <>Jste přihlášen jako <strong>správce podpory (superadmin)</strong> v systému klienta{sinceTxt}.</>
        ) : (
          <>K tomuto systému je právě připojen <strong>správce podpory (superadmin)</strong>{sinceTxt}.</>
        )}
      </span>
      {isSuperadmin && (
        <button onClick={endSession} disabled={ending}
                className="inline-flex items-center gap-1.5 bg-amber-950/90 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:bg-amber-950 disabled:opacity-60 shrink-0">
          {ending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Ukončit relaci
        </button>
      )}
    </div>
  );
}
